import type { ReelJobEvent } from '../../../shared/api/reels.api';
import { serverEventToStatus, type ServerProgressStatus } from './serverProgress';

// Pure reducer for the resume path (page reloaded mid-publish). Maps a single
// polled ReelJobEvent to the overall progress + per-account update, tracking the
// running terminal count so the bar reflects the *real* backend phase/percent
// instead of restarting from 0. Mirrors the live-pipeline handling so a resumed
// tab and the original tab show the same progress.
export interface ResumeEventResult {
  // Overall progress derived from this event (null = no bar impact).
  overall: ServerProgressStatus | null;
  // Running count of accounts that reached a terminal status (success/error).
  publishedCount: number;
  // Per-account terminal update, or null when this event is not a terminal
  // per-account transition (e.g. retrying, render, publish pulse).
  account: { index: number; status: 'success' | 'error'; error?: string } | null;
}

export function applyResumeEvent(
  event: ReelJobEvent,
  accountIds: string[],
  publishedCount: number,
): ResumeEventResult {
  // A terminal per-account publish event advances the count BEFORE we compute
  // the bar (serverEventToStatus reads publishedCount). 'retrying' is explicitly
  // not terminal — it must not count or flip the tile.
  if (
    event.phase === 'publish' &&
    event.accountId &&
    (event.status === 'success' || event.status === 'error')
  ) {
    const index = accountIds.indexOf(event.accountId);
    if (index < 0) {
      // Unknown account — ignore, but still let the bar react to the event.
      return { overall: serverEventToStatus(event, { total: accountIds.length, publishedCount }), publishedCount, account: null };
    }
    const nextCount = publishedCount + 1;
    return {
      overall: serverEventToStatus(event, { total: accountIds.length, publishedCount: nextCount }),
      publishedCount: nextCount,
      account: { index, status: event.status, error: event.error },
    };
  }

  // Non-terminal events (render, upload, publish pulse, retrying, done) only
  // move the bar; publishedCount is unchanged and no tile flips.
  return {
    overall: serverEventToStatus(event, { total: accountIds.length, publishedCount }),
    publishedCount,
    account: null,
  };
}
